import clsx from 'clsx';
import { AlertCircle, AlertTriangle, Bell, CheckCircle2, Loader2, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api';

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/* ─────────────────────────── Buttons ─────────────────────────── */

export function Spinner({ className }: { className?: string }) {
  return <Loader2 size={16} className={clsx('animate-spin', className)} aria-hidden />;
}

/* ─────────────────────────── Form field ─────────────────────────── */

export function Field({ label, hint, error, children, required }: { label: string; hint?: string; error?: string; required?: boolean; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
        {required ? <span className="text-danger-fg"> *</span> : null}
      </label>
      {children(id)}
      {error ? (
        <p className="mt-1 flex items-center gap-1 text-xs text-danger-fg" role="alert">
          <AlertCircle size={12} aria-hidden /> {error}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-text-faint">{hint}</p>
      ) : null}
    </div>
  );
}

/* ─────────────────────────── Badge / status pill ─────────────────────────── */

const TONES = {
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  danger: 'bg-danger-bg text-danger-fg',
  neutral: 'bg-neutral-bg text-neutral-fg',
  info: 'bg-info-bg text-info-fg',
  brand: 'bg-brand-soft text-brand-ink',
} as const;

export function Badge({ tone = 'neutral', children, dot }: { tone?: keyof typeof TONES; children: ReactNode; dot?: boolean }) {
  return (
    <span className={clsx('pill', TONES[tone])}>
      {dot ? <span className="size-1.5 rounded-full bg-current" aria-hidden /> : null}
      {children}
    </span>
  );
}

/* ─────────────────────────── Dialog ─────────────────────────── */

export function Dialog({ open, onClose, title, children, footer, size = 'md' }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; size?: 'md' | 'lg' }) {
  const { t } = useTranslation();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Focus trap + restore: move focus into the dialog on open, cycle Tab/Shift+Tab within it,
  // and return focus to whatever triggered it on close — a plain click-through-to-body dialog
  // is a keyboard/screen-reader trap otherwise.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const focusables = () => Array.from(panel?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    (focusables()[0] ?? panel)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (!items.length) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      restoreFocusRef.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-6" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-label={title} className={clsx('card flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-b-none outline-none sm:rounded-b-2xl', size === 'lg' ? 'sm:max-w-3xl' : 'sm:max-w-lg')}>
        <div className="flex items-center gap-3 border-b border-border-soft px-5 py-4">
          <h2 className="flex-1 text-base font-bold">{title}</h2>
          <button className="btn-ghost -me-2 size-9 !p-0" onClick={onClose} aria-label={t('common.close')}>
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer ? <div className="flex justify-end gap-2 border-t border-border-soft px-5 py-3">{footer}</div> : null}
      </div>
    </div>
  );
}

/* ─────────────────────────── Toasts ─────────────────────────── */

interface Toast {
  id: number;
  tone: 'success' | 'error' | 'info';
  text: string;
}
const ToastCtx = createContext<{ push: (t: Omit<Toast, 'id'>) => void }>({ push: () => {} });

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Toast[]>([]);
  const push = useCallback((t: Omit<Toast, 'id'>) => {
    const id = Date.now() + Math.random();
    setItems((s) => [...s, { ...t, id }]);
    setTimeout(() => setItems((s) => s.filter((x) => x.id !== id)), 4000);
  }, []);
  const value = useMemo(() => ({ push }), [push]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed bottom-4 start-4 z-[60] flex flex-col gap-2" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={clsx('pointer-events-auto flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium shadow-overlay', t.tone === 'success' ? 'bg-success-fg text-white' : t.tone === 'info' ? 'bg-brand text-white' : 'bg-danger-fg text-white')}>
            {t.tone === 'success' ? <CheckCircle2 size={16} /> : t.tone === 'info' ? <Bell size={16} /> : <AlertCircle size={16} />}
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const { push } = useContext(ToastCtx);
  const { i18n } = useTranslation();
  return {
    success: (text: string) => push({ tone: 'success', text }),
    info: (text: string) => push({ tone: 'info', text }),
    error: (e: unknown) => push({ tone: 'error', text: e instanceof ApiError ? e.localized : e instanceof Error ? e.message : i18n.language === 'en' ? 'Something went wrong' : 'حدث خطأ' }),
  };
}

/* ─────────────────────────── Confirm dialog (replaces window.confirm) ─────────────────────────── */

interface ConfirmOptions {
  title?: string;
  danger?: boolean;
}
interface ConfirmState extends ConfirmOptions {
  message: string;
  resolve: (ok: boolean) => void;
}
const ConfirmCtx = createContext<(message: string, options?: ConfirmOptions) => Promise<boolean>>(async () => false);

/** A native `confirm()` blocks the whole tab and can't be themed, translated consistently, or trapped for a11y. Mount once near the app root. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const [state, setState] = useState<ConfirmState | null>(null);

  const confirmDialog = useCallback((message: string, options?: ConfirmOptions) => new Promise<boolean>((resolve) => setState({ message, resolve, ...options })), []);

  const settle = (ok: boolean) => {
    state?.resolve(ok);
    setState(null);
  };

  return (
    <ConfirmCtx.Provider value={confirmDialog}>
      {children}
      <Dialog
        open={!!state}
        onClose={() => settle(false)}
        title={state?.title ?? t('common.confirm')}
        footer={
          <>
            <button className="btn-secondary" onClick={() => settle(false)}>
              {t('common.cancel')}
            </button>
            <button className={state?.danger ? 'btn-danger' : 'btn-primary'} onClick={() => settle(true)} autoFocus>
              {t('common.confirm')}
            </button>
          </>
        }
      >
        <p className="flex items-start gap-2 text-sm">
          {state?.danger ? <AlertTriangle size={18} className="mt-0.5 shrink-0 text-danger-fg" aria-hidden /> : null}
          {state?.message}
        </p>
      </Dialog>
    </ConfirmCtx.Provider>
  );
}

/** `const confirmDialog = useConfirm(); if (await confirmDialog(t('...'), { danger: true })) { ... }` */
export function useConfirm() {
  return useContext(ConfirmCtx);
}

/* ─────────────────────────── Table / empty / skeleton ─────────────────────────── */

export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={clsx('card overflow-x-auto', className)}>
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}
/** `sticky="end"` pins the cell to the trailing edge while the table scrolls horizontally (rows should carry `group` so hover tint matches). */
type CellProps = { children?: ReactNode; className?: string; sticky?: 'end' };
const stickyEnd = 'sticky end-0 z-[1] bg-surface group-hover:bg-surface-hover border-s border-border-soft';
export const Th = ({ children, className, sticky }: CellProps) => (
  <th className={clsx('whitespace-nowrap px-4 py-2.5 text-start text-xs font-medium text-text-muted', sticky && stickyEnd, className)}>{children}</th>
);
export const Td = ({ children, className, sticky }: CellProps) => <td className={clsx('border-t border-border-soft px-4 py-3 align-middle', sticky && stickyEnd, className)}>{children}</td>;

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
      <p className="font-semibold">{title}</p>
      {hint ? <p className="text-sm text-text-muted">{hint}</p> : null}
      {action}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('animate-pulse rounded-md bg-surface-2', className)} aria-hidden />;
}

export function PageHeader({ title, subtitle, actions }: { title: ReactNode; subtitle?: string; actions?: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="min-w-0 flex-1">
        <h2 className="text-lg font-bold">{title}</h2>
        {subtitle ? <p className="text-sm text-text-muted">{subtitle}</p> : null}
      </div>
      {actions}
    </div>
  );
}

export function Pagination({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex items-center justify-between px-4 py-2.5 text-xs text-text-muted">
      <span>{t('common.totalCount', { count: total })}</span>
      <div className="flex items-center gap-1">
        <button className="btn-ghost !min-h-8 !px-2.5" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          {t('common.prev')}
        </button>
        <span className="px-2 font-semibold text-text">
          {page} / {pages}
        </span>
        <button className="btn-ghost !min-h-8 !px-2.5" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          {t('common.next')}
        </button>
      </div>
    </div>
  );
}
